import Modal from 'flowbite/lib/esm/components/modal/index.js';

const DEFAULT_OPTIONS = {
  placement: 'center',
  backdrop: 'dynamic',
  backdropClasses: 'bg-overlay fixed inset-0 z-40',
};

function ensureModalInstance(modalEl) {
  if (modalEl.__modalInstance) return modalEl.__modalInstance;
  const modalId = modalEl.id || 'global-iframe-modal';
  const modal = new Modal(modalEl, DEFAULT_OPTIONS, { id: modalId, override: true });
  modalEl.__modalInstance = modal;
  return modal;
}

function renderIframeMarkup(host, src, type = '') {
  const isPano = type === 'pano';
  const wrapClass = isPano ? 'w-full' : 'w-full px-0 md:px-24 2xl:px-32';
  const iframeClass = isPano ? 'w-full h-full' : 'w-full h-full aspect-[16/9]';
  host.innerHTML = `
    <div class="${wrapClass}" data-modal-iframe-wrap data-iframe-type="${type}">
      <iframe
        class="${iframeClass}"
        data-runtime-iframe
        src="${src}"
        loading="lazy"
        style="border:0;"
        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
        allowfullscreen="true"
      ></iframe>
    </div>
  `;
}

function clearRuntimeIframes(container) {
  if (!container) return;
  container.querySelectorAll('iframe').forEach((iframe) => {
    if (iframe.getAttribute('src')) {
      iframe.setAttribute('src', 'about:blank');
      iframe.setAttribute('src', '');
    }
  });
}

function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true');
}

function focusModalCloseButton(modalEl) {
  if (!modalEl) return;
  const closeButton = modalEl.querySelector('[data-focus-first], [data-modal-hide]');
  if (closeButton && typeof closeButton.focus === 'function') {
    closeButton.focus({ preventScroll: true });
  }
}

function setupModalKeyboardFocus(modalEl) {
  if (!modalEl || modalEl.dataset.modalFocusBound === '1') return;

  const focusFirst = () => {
    const first = modalEl.querySelector('[data-focus-first]') || getFocusableElements(modalEl)[0] || modalEl;
    if (first && typeof first.focus === 'function') first.focus({ preventScroll: true });
  };

  modalEl.addEventListener('show.tw.modal', () => {
    modalEl.__lastFocusedElement = document.activeElement;
    requestAnimationFrame(focusFirst);
  });

  modalEl.addEventListener('hidden.tw.modal', () => {
    const restore = modalEl.__lastFocusedElement;
    if (restore && typeof restore.focus === 'function' && document.contains(restore)) {
      restore.focus({ preventScroll: true });
    }
  });

  modalEl.addEventListener('keydown', (event) => {
    if (modalEl.classList.contains('hidden')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      if (typeof modalEl.__cleanupGalleryContent === 'function') {
        modalEl.__cleanupGalleryContent();
      }
      if (typeof modalEl.__cleanupIframeContent === 'function') {
        modalEl.__cleanupIframeContent();
      }
      ensureModalInstance(modalEl).hide();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusables = getFocusableElements(modalEl);
    if (!focusables.length) {
      event.preventDefault();
      modalEl.focus({ preventScroll: true });
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === modalEl)) {
      event.preventDefault();
      last.focus({ preventScroll: true });
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  });

  modalEl.dataset.modalFocusBound = '1';
}

export default function initIframeModal() {
  const body = document.body;
  if (
    body?.classList.contains('wp-admin') ||
    body?.classList.contains('block-editor-page') ||
    body?.classList.contains('block-editor-iframe__body')
  ) {
    return;
  }

  const modalEl = document.getElementById('global-iframe-modal');
  if (!modalEl) return;
  setupModalKeyboardFocus(modalEl);

  const mediaHost = modalEl.querySelector('[data-modal-media-host]');
  const titleEl = modalEl.querySelector('[data-iframe-modal-title]');
  if (!mediaHost) return;

  const modal = ensureModalInstance(modalEl);

  const cleanup = () => {
    clearRuntimeIframes(mediaHost);
    mediaHost.innerHTML = '';
    mediaHost.classList.add('hidden');
    modalEl.dataset.modalContent = '';
    modalEl.dataset.iframeType = '';
    modalEl.setAttribute('aria-label', 'Medieninhalte');
  };
  modalEl.__cleanupIframeContent = cleanup;

  if (modalEl.dataset.iframeCleanupBound !== '1') {
    modalEl.addEventListener('hide.tw.modal', cleanup);
    modalEl.addEventListener('hidden.tw.modal', cleanup);
    modalEl.dataset.iframeCleanupBound = '1';
  }

  if (modalEl.dataset.iframeCleanupObserverBound !== '1') {
    const observer = new MutationObserver(() => {
      if (modalEl.classList.contains('hidden')) {
        if (typeof modalEl.__cleanupGalleryContent === 'function') {
          modalEl.__cleanupGalleryContent();
        }
        if (typeof modalEl.__cleanupIframeContent === 'function') {
          modalEl.__cleanupIframeContent();
        }
      }
    });
    observer.observe(modalEl, { attributes: true, attributeFilter: ['class'] });
    modalEl.dataset.iframeCleanupObserverBound = '1';
  }

  if (modalEl.dataset.iframeCloseBound !== '1') {
    modalEl.querySelectorAll('[data-modal-hide]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        if (typeof modalEl.__cleanupGalleryContent === 'function') {
          modalEl.__cleanupGalleryContent();
        }
        if (typeof modalEl.__cleanupIframeContent === 'function') {
          modalEl.__cleanupIframeContent();
        }
        ensureModalInstance(modalEl).hide();
      });
    });
    modalEl.dataset.iframeCloseBound = '1';
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-modal-target]');
    if (!trigger) return;

    const targetId = trigger.dataset.modalTarget;
    if (!targetId || targetId !== modalEl.id) return;

    const nextSrc = trigger.dataset.iframeSrc || '';
    if (!nextSrc) return;
    const iframeType = trigger.dataset.iframeType || '';

    if (typeof modalEl.__cleanupGalleryContent === 'function') {
      modalEl.__cleanupGalleryContent();
    }
    if (typeof modalEl.__cleanupIframeContent === 'function') {
      modalEl.__cleanupIframeContent();
    }

    mediaHost.classList.remove('hidden');
    modalEl.dataset.modalContent = 'iframe';
    modalEl.dataset.iframeType = iframeType;

    renderIframeMarkup(mediaHost, nextSrc, iframeType);

    if (trigger.dataset.iframeLabel) {
      modalEl.setAttribute('aria-label', trigger.dataset.iframeLabel);
    } else {
      modalEl.setAttribute('aria-label', 'Medieninhalte');
    }

    if (titleEl) {
      const nextTitle = trigger.dataset.iframeTitle || '';
      titleEl.textContent = nextTitle;
      titleEl.hidden = nextTitle === '';
    }

    modal._options.backdrop = DEFAULT_OPTIONS.backdrop;
    modal._options.backdropClasses = DEFAULT_OPTIONS.backdropClasses;
    modal.show();
    requestAnimationFrame(() => focusModalCloseButton(modalEl));
  });
}
